import React, { useState, useEffect, useRef } from 'react';
import { MapPin, ChevronDown, X } from 'lucide-react';
import * as api from '../api';

/**
 * Filtre multi-sélection par département (préfixe de code postal) — combobox + pills.
 * Règle de dérivation côté backend : CP commençant par 97/98 (DOM-TOM) → 3 chiffres
 * (97110 → 971), sinon 2 chiffres (75001 → 75, 01000 → 01, 92100 → 92).
 * Logique inclusive : sélectionner 971 + 75 n'affiche que ces deux départements.
 * Un tiers sans CP valide est exclu dès qu'au moins un département est sélectionné.
 *
 * Props :
 *  - value: string[]              codes département sélectionnés (ex. ['971', '75'])
 *  - onChange: (codes) => void
 *  - testidPrefix: string         préfixe des data-testid (défaut: 'dept')
 */
export default function DepartementFilter({ value = [], onChange, testidPrefix = 'dept' }) {
  const [options, setOptions] = useState([]);
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState('');
  const ref      = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.getAnnuaireDepartements()
      .then(r => setOptions(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = options.filter(o =>
    !value.includes(o.code) &&
    (!q || o.code.startsWith(q) || (o.nom || '').toLowerCase().includes(q))
  );

  const add      = (code) => { onChange([...value, code]); setQuery(''); inputRef.current?.focus(); };
  const remove   = (code) => onChange(value.filter(c => c !== code));
  const labelFor = (code) => {
    const opt = options.find(o => o.code === code);
    return opt?.nom ? `${code} — ${opt.nom}` : code;
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" ref={ref} data-testid={`${testidPrefix}-filter`}>
      <MapPin size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />

      {/* Pills des départements sélectionnés */}
      {value.map(code => (
        <span key={code} data-testid={`${testidPrefix}-pill-${code}`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold"
          style={{ background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE' }}>
          {labelFor(code)}
          <button type="button" onClick={() => remove(code)}
            data-testid={`${testidPrefix}-pill-remove-${code}`}
            className="hover:opacity-60 transition-opacity" title="Retirer">
            <X size={10} />
          </button>
        </span>
      ))}

      {/* Combobox */}
      <div className="relative">
        <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border transition-colors"
          style={{
            borderColor: value.length > 0 ? '#4338CA' : 'var(--border)',
            background:  value.length > 0 ? '#EEF2FF' : 'white',
          }}>
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Département (ex. 971, 75…)"
            data-testid={`${testidPrefix}-input`}
            className="w-44 bg-transparent outline-none text-xs"
            style={{ color: 'var(--text)' }}
          />
          <ChevronDown size={10}
            style={{ color: 'var(--text-muted)', transition: 'transform .15s',
                     transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
        </div>

        {open && (
          <div className="absolute z-50 mt-1 rounded-xl shadow-lg border bg-white py-1 w-72"
            style={{ borderColor: 'var(--border)', top: '100%', left: 0, maxHeight: '260px', overflowY: 'auto' }}
            data-testid={`${testidPrefix}-menu`}>
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                Aucun département trouvé
              </div>
            ) : filtered.map(o => (
              <button key={o.code} type="button"
                onClick={() => add(o.code)}
                data-testid={`${testidPrefix}-option-${o.code}`}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 transition-colors"
                style={{ color: 'var(--text)' }}>
                <span className="font-semibold tabular-nums" style={{ minWidth: '2rem' }}>{o.code}</span>
                <span className="flex-1 truncate" style={{ color: 'var(--text-muted)' }}>{o.nom || ''}</span>
                <span className="tabular-nums" style={{ color: 'var(--text-muted)' }}>{o.nb}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
